import { useFormikContext } from 'formik';

import { ErrorField } from '@/components/error-field';
import { RequiredLabel } from '@/components/required-label';
import { DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import { useCreateKnowledgeBaseMutation } from '@/query/knowledge.query';

import type { CreateKnowledgeBaseFormValues } from '@/schemas/knowledge-base.schema';

import { useFormikError } from '@/lib/error';

import { KnowledgeBaseActions } from './document-upload-actions';

import { CreateKnowledgeBaseFormProvider } from '@/formik/knowledge-base.formik';
import { useKnowledgeModalsStore } from '@/store/knowledge-modals.store';
import useModalStore from '@/store/modal.store';

/**
 * Isolated Formik-connected name field.
 * This is the ONLY component that lives inside FormikProvider and re-renders on keystrokes.
 * It syncs Formik state out to parent refs so nothing else needs to subscribe.
 */
function KnowledgeBaseNameField({ disabled }: { disabled: boolean }) {
  const { getFieldProps } = useFormikContext<CreateKnowledgeBaseFormValues>();
  const hasError = useFormikError();

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Field aria-invalid={hasError('name')}>
          <RequiredLabel htmlFor="name-input">Knowledge Base Name</RequiredLabel>
          <Input
            id="name-input"
            type="text"
            placeholder="Enter knowledge base name"
            aria-invalid={hasError('name')}
            {...getFieldProps('name')}
            className="h-9"
            disabled={disabled}
          />
          <ErrorField name="name" />
        </Field>
      </div>
      <div className="space-y-2">
        <Field aria-invalid={hasError('kb_description')}>
          <RequiredLabel htmlFor="kb-description-input">Description</RequiredLabel>
          <Textarea
            id="kb-description-input"
            placeholder="Describe what this knowledge base contains"
            aria-invalid={hasError('kb_description')}
            {...getFieldProps('kb_description')}
            className="resize-none"
            rows={3}
            disabled={disabled}
          />
          <ErrorField name="kb_description" />
        </Field>
      </div>
    </div>
  );
}

export function CreateKnowledgeBaseDialog() {
  const selectKnowledgeBase = useKnowledgeModalsStore(state => state.selectKnowledgeBase);
  const closeModal = useModalStore(state => state.closeModal);

  const handleClose = () => {
    closeModal();
  };

  const createKnowledgeBaseMutation = useCreateKnowledgeBaseMutation({
    onSuccess: createdKb => {
      selectKnowledgeBase(createdKb);
      handleClose();
    },
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-lg font-semibold">Add Knowledge Base</DialogTitle>
      </DialogHeader>

      <CreateKnowledgeBaseFormProvider onSubmit={createKnowledgeBaseMutation.mutate}>
        <div className="space-y-4">
          <KnowledgeBaseNameField disabled={createKnowledgeBaseMutation.isPending} />
        </div>

        <KnowledgeBaseActions
          onCancel={handleClose}
          isLoading={createKnowledgeBaseMutation.isPending}
        />
      </CreateKnowledgeBaseFormProvider>
    </>
  );
}
